import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

// Component for slow-motion video with key for refresh
const SlowVideo = ({ src, poster, className }) => {
  const videoRef = useRef(null);
  
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.6;
      videoRef.current.load();
    }
  }, [src]);
  
  return (
    <video
      ref={videoRef}
      key={src}
      autoPlay
      muted
      loop
      playsInline
      className={className}
      poster={poster}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
};

const ExperienceSection = () => {
  const { t, getCustomMedia } = useLanguage();
  
  const experiences = [
    {
      id: 'heat',
      // Feuer/Flamme Video - Pexels 18991921 (HD format works)
      video: 'https://videos.pexels.com/video-files/18991921/18991921-hd_1920_1080_25fps.mp4',
      poster: 'https://images.unsplash.com/photo-1518245856972-a0333f3f8293?w=1920&q=80',
      color: 'rgba(255, 100, 50, 0.1)',
      glowColor: 'rgba(255, 80, 30, 0.2)',
      numberKey: 'heat.number',
      titleKey: 'heat.title',
      subtitleKey: 'heat.subtitle',
      descriptionKey: 'heat.description',
      statKey: 'heat.temp',
      statLabel: 'Temperature',
    },
    {
      id: 'cold',
      // Winter Bach mit Schnee - Pexels 14171679 (vertikales Video, wird zugeschnitten)
      video: 'https://videos.pexels.com/video-files/14171679/14171679-hd_1080_1920_24fps.mp4',
      poster: 'https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=1920&q=80',
      color: 'rgba(43, 254, 205, 0.05)',
      glowColor: 'rgba(43, 254, 205, 0.1)',
      numberKey: 'cold.number',
      titleKey: 'cold.title',
      subtitleKey: 'cold.subtitle',
      descriptionKey: 'cold.description',
      statKey: 'cold.temp',
      statLabel: 'Temperature',
    },
    {
      id: 'rest',
      // Mystischer Wald - Pexels 1448735 (forest with waterfall)
      video: 'https://videos.pexels.com/video-files/1448735/1448735-uhd_2732_1440_24fps.mp4',
      poster: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=1920&q=80',
      color: 'rgba(26, 47, 35, 0.15)',
      glowColor: 'rgba(100, 150, 100, 0.1)',
      numberKey: 'rest.number',
      titleKey: 'rest.title',
      subtitleKey: 'rest.subtitle',
      descriptionKey: 'rest.description',
      statKey: 'rest.element',
      statLabel: 'Element',
    },
  ];
  
  return (
    <section id="experience" data-testid="experience-section">
      {experiences.map((exp, index) => {
        const customVideo = getCustomMedia(exp.id, 'video');
        const videoUrl = customVideo || exp.video;
        
        return (
          <div 
            key={exp.id}
            className="relative min-h-screen w-full overflow-hidden"
            data-testid={`experience-${exp.id}`}
          >
            {/* Video Background */}
            <div className="absolute inset-0">
              <SlowVideo
                src={videoUrl}
                poster={exp.poster}
                className="w-full h-full object-cover opacity-60"
              />
              {/* Mystische Verläufe für sanfte Übergänge */}
              <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/70" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.7)_100%)]" />
              <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at center bottom, ${exp.glowColor} 0%, transparent 50%)` }} />
            </div>
            
            {/* Large Background Number */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <motion.span 
                className="font-cinzel text-[20rem] md:text-[30rem] text-white/[0.03] select-none"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2 }}
              >
                {t(exp.numberKey)}
              </motion.span>
            </div>
            
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center min-h-screen px-6 py-24">
              <div className="max-w-4xl text-center">
                {/* Roman Numeral */}
                <motion.span 
                  className="block font-cinzel text-6xl md:text-8xl text-[#2bfecd]/30 mb-8"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 1.2 }}
                >
                  {t(exp.numberKey)}
                </motion.span>
                
                {/* Title */}
                <motion.h2 
                  className="font-cinzel text-5xl md:text-7xl lg:text-8xl tracking-[0.2em] uppercase mb-6 text-white"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 1.2, delay: 0.2 }}
                >
                  {t(exp.titleKey)}
                </motion.h2>
                
                {/* Subtitle */}
                <motion.p 
                  className="text-sm md:text-base tracking-[0.4em] uppercase text-[#2bfecd] mb-10"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.4 }}
                  style={{ textShadow: '0 0 30px rgba(43, 254, 205, 0.5)' }}
                >
                  {t(exp.subtitleKey)}
                </motion.p>
                
                {/* Decorative Line */}
                <motion.div 
                  className="flex items-center justify-center gap-4 mb-10"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.5 }}
                >
                  <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#2bfecd]/40" />
                  <div className="w-2 h-2 rotate-45 border border-[#2bfecd]/40" />
                  <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#2bfecd]/40" />
                </motion.div>
                
                {/* Description */}
                <motion.p 
                  className="text-base md:text-lg leading-[2] tracking-wide text-white/60 max-w-2xl mx-auto mb-12"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.6 }}
                >
                  {t(exp.descriptionKey)}
                </motion.p>
                
                {/* Stat */}
                <motion.div 
                  className="inline-block"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.8 }}
                >
                  <span className="block font-cinzel text-4xl md:text-5xl text-[#2bfecd] mb-2">
                    {t(exp.statKey)}
                  </span>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-white/30">
                    {exp.statLabel}
                  </span>
                </motion.div>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
};

export default ExperienceSection;
